import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { BaseTable, TableColumn } from '../../common/Table';
import type { TableItem } from '../../common/Table/Table.hooks';
import { ActionButton } from '../../common/Table/Table.styles';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import type { SoldItemListRow } from '../../../services/soldItems.service';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatCommercialSaleDate } from '../../../utils/sales/commercial-sale-date';
import { getSaleInvoiceLabel } from '../../../utils/sales/sale-invoice.utils';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../utils/sales/branch-receipt-numbering';
import { useRelatedEntityNavigation } from '../../../hooks/useRelatedEntityNavigation';
import { OrderCodeButton, TotalText } from '../orders/OrdersTable/OrdersTable.styles';

/** BaseTable exige `id`; el API usa `salesOrderItemId`. */
type SoldItemTableRow = SoldItemListRow & TableItem;

interface SalesByPieceTableProps {
  data: SoldItemListRow[];
  onView: (row: SoldItemListRow) => void;
}

const colWidth = (
  px: number,
): Pick<TableColumn<SoldItemTableRow>, 'headerStyle' | 'cellStyle'> => ({
  headerStyle: { minWidth: px },
  cellStyle: { minWidth: px },
});

const SalesByPieceTable: React.FC<SalesByPieceTableProps> = ({ data, onView }) => {
  const { navigateToRelatedEntity } = useRelatedEntityNavigation();

  const tableRows: SoldItemTableRow[] = data.map((row) => ({
    ...row,
    id: row.salesOrderItemId,
  }));

  const columns: TableColumn<SoldItemTableRow>[] = [
    {
      id: 'productCodeUnique',
      label: 'Código',
      ...colWidth(140),
    },
    {
      id: 'categoryName',
      label: 'Tipo',
      ...colWidth(120),
    },
    {
      id: 'orderCode',
      label: SALES_ORDER_NUMBER_LABEL,
      ...colWidth(130),
      render: (row) => (
        <OrderCodeButton
          component="button"
          type="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            navigateToRelatedEntity('SALES_ORDER', row.orderId, row.orderCode);
          }}
        >
          {row.orderCode}
        </OrderCodeButton>
      ),
    },
    ...(BRANCH_RECEIPT_NUMBERING_ENABLED
      ? [
          {
            id: 'branchReceiptCode',
            label: BRANCH_RECEIPT_NUMBER_LABEL,
            ...colWidth(130),
            sortAccessor: (row: SoldItemTableRow) => row.branchReceiptCode ?? '',
            render: (row: SoldItemTableRow) => row.branchReceiptCode ?? '—',
          } as TableColumn<SoldItemTableRow>,
        ]
      : []),
    {
      id: 'saleDate',
      label: 'Fecha de Venta',
      render: (row) =>
        formatCommercialSaleDate(
          {
            saleDate: row.saleDate,
            createdAt: row.saleDate,
          },
          'es-ES',
          { includeTime: true },
        ),
      ...colWidth(150),
    },
    {
      id: 'customer',
      label: 'Cliente',
      sortAccessor: (row) => (row.hasCustomer ? (row.customerName ?? 'Sí') : 'No'),
      render: (row) => (row.hasCustomer ? (row.customerName ?? 'Sí') : 'No'),
      ...colWidth(120),
    },
    {
      id: 'hasInvoice',
      label: 'Factura',
      sortAccessor: (row) => (row.hasInvoice ? 1 : 0),
      render: (row) => getSaleInvoiceLabel(row.hasInvoice),
      ...colWidth(110),
    },
    {
      id: 'finalPrice',
      label: 'Precio de Venta',
      render: (row) => <TotalText variant="body2">{formatCurrency(row.finalPrice)}</TotalText>,
      align: 'right',
      ...colWidth(120),
    },
    {
      id: 'sellerDisplayName',
      label: 'Vendedor',
      ...colWidth(130),
    },
    {
      id: 'locationName',
      label: 'Ubicación',
      ...colWidth(120),
    },
    {
      id: 'daysUntilSale',
      label: 'Días hasta la venta',
      render: (row) => row.daysUntilSale,
      align: 'right',
      ...colWidth(130),
    },
  ];

  return (
    <BaseTable
      columns={columns}
      items={tableRows}
      getItemId={(row) => row.salesOrderItemId}
      cellTone="muted"
      showActions
      emptyStateTitle="No hay piezas vendidas con estos filtros."
      renderActions={(row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Ver detalle" placement="top" arrow>
            <ActionButton
              size="small"
              onClick={() => onView(row)}
              aria-label={`Ver detalle de ${row.productCodeUnique}`}
            >
              <VisibilityIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        </Box>
      )}
    />
  );
};

export default SalesByPieceTable;
